import { Observable, Subscription, fromEvent } from 'rxjs';
import { filter, first, map, takeWhile } from 'rxjs/operators';

import {
  Broadcaster,
  EmbedMessageEvent,
  RequestMessage,
  Requests,
  ResponseMessage,
  Responses,
  RpcTransport,
} from './types';

const RPC_TIMEOUT_MS = 30000;

let currentRequestId = 1; // lazy id maintenance because javascript is singlethreaded
function nextRequestId() {
  currentRequestId = (currentRequestId + 1) % 1000000; // needs to recycle eventually
  return currentRequestId;
}

interface SentRpcRequest<T extends Requests> {
  request: T;
  requestId: number;
}

function isRpcRequest(msg: EmbedMessageEvent, requestType: Requests['type']): boolean {
  return msg != null && msg.data != null && msg.data.type === 'rpcRequest' && msg.data.request.type === requestType;
}

function isMatchingResponse(msg: EmbedMessageEvent, requestId: number): boolean {
  return msg != null && msg.data.type === 'rpcResponse' && msg.data.requestId === requestId;
}

export const Rpc: RpcTransport = {
  getRpcRequestStream: function <T extends Requests>(
    source: EventTarget,
    type: T['type']
  ): Observable<SentRpcRequest<T> | null> {
    return fromEvent<EmbedMessageEvent>(source, 'message').pipe(
      takeWhile(() => source != null),
      filter((msg) => isRpcRequest(msg, type)),
      map<EmbedMessageEvent, SentRpcRequest<T> | null>((msg) =>
        msg.data.type === 'rpcRequest' ? { request: msg.data.request as T, requestId: msg.data.requestId } : null
      )
    );
  },

  sendRequest: async function <TResponse extends Responses>(
    source: EventTarget,
    target: Broadcaster,
    request: Requests
  ): Promise<TResponse> {
    const requestId = nextRequestId();
    const response$ = fromEvent<EmbedMessageEvent>(source, 'message').pipe(
      takeWhile(() => source != null && target != null),
      filter((msg) => isMatchingResponse(msg, requestId)),
      map((msg) => (msg.data.type === 'rpcResponse' ? (msg.data.response as TResponse) : null)),
      first()
    );

    let subscription: Subscription;

    return await new Promise<TResponse>((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (subscription != null) {
          subscription.unsubscribe();
        }

        reject(new Error('request timed out'));
      }, RPC_TIMEOUT_MS);

      const requestMessage: RequestMessage = {
        type: 'rpcRequest',
        requestId,
        request,
      };

      if (target != null) {
        subscription = response$.subscribe((ret) => {
          clearTimeout(timeout);
          // should error returns reject?
          resolve(ret as TResponse);
        });

        // TODO: Fix origin
        target.postMessage(requestMessage, '*');
      } else {
        // if target is empty, probably it was closed
        // just clear timeout and ignore
        clearTimeout(timeout);
      }
    });
  },

  sendResponse: function (target: Broadcaster, requestId: number, response: Responses) {
    const responseMessage: ResponseMessage = {
      type: 'rpcResponse',
      requestId,
      response,
    };

    // TODO: Fix origin
    target.postMessage(responseMessage, '*');
  },
};
